import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';

/**
 * Rollaxeln (ortogonal mot persona-axeln). Rollvalet görs medvetet vid login via
 * RoleGate/RoleSelectScreen och persisteras per användare i localStorage under
 * nyckeln `app:role:<user.tag>`.
 *
 * `app:role:<tag>` är KÄLLAN (tre tillstånd: 'supervisor' | 'user' | null).
 * `isSupervising` / localStorage['supervising'] förblir den interna mekaniken som
 * driver effectiveChildMode-omräkningen. Rollvalet skriver ALDRIG 'supervising'
 * direkt — det går alltid via SupervisorContext.setSupervising, så
 * 'storage'-event-fixen och temaomräkningen behålls intakt (inget barn-läckage).
 *
 * Rollstate ligger i en Context (RoleProvider), INTE i lokalt useState per
 * konsument — annars har varje useRole()-anrop sin egen kopia och t.ex.
 * clearRole() i RoleSwitchSetting syns aldrig i RoleGates separata instans
 * (samma mönster som SupervisorContext använder för isSupervising).
 */

export type AppRole = 'supervisor' | 'user';

const roleKey = (tag?: string): string | null => (tag ? `app:role:${tag}` : null);

const readRole = (tag?: string): AppRole | null => {
  const key = roleKey(tag);
  if (!key) return null;
  const raw = localStorage.getItem(key);
  return raw === 'supervisor' || raw === 'user' ? raw : null;
};

export type UseRoleResult = {
  /** Det persisterade rollvalet för aktuell användare, eller null om inget val gjorts. */
  role: AppRole | null;
  /** Sätter och persisterar rollvalet och driver isSupervising via setSupervising. */
  setRole: (role: AppRole) => void;
  /** Rensar rollvalet → RoleGate visar rollväljaren igen vid nästa render. */
  clearRole: () => void;
};

const noop = () => {};

export const RoleContext = createContext<UseRoleResult>({
  role: null,
  setRole: noop,
  clearRole: noop,
});

type RoleProviderProps = {
  children?: ReactNode;
};

/**
 * Mountas en gång (i AuthGuard, inuti SupervisorScene) och äger den delade
 * rollstaten. Alla useRole()-konsumenter läser samma Context-värde, så
 * setRole/clearRole i en komponent syns omedelbart i alla andra.
 */
export const RoleProvider = ({ children }: RoleProviderProps) => {
  const { user } = useContext(AppContext);
  const { setSupervising } = useContext(SupervisorContext);
  const tag = user?.tag;

  const [role, setRoleState] = useState<AppRole | null>(() => readRole(tag));

  // Synka state om användaren byts (delad enhet/kontobyte) — håller rollvalet
  // kopplat till rätt tag och undviker läckage mellan konton.
  useEffect(() => {
    setRoleState(readRole(tag));
  }, [tag]);

  const setRole = useCallback(
    (next: AppRole) => {
      const key = roleKey(tag);
      if (key) localStorage.setItem(key, next);
      setRoleState(next);
      // Driv den befintliga supervising-mekaniken. setSupervising(false) rensar
      // currentClient och dispatchar 'storage' så temat räknas om (inget barn-läckage).
      setSupervising(next === 'supervisor');
    },
    [tag, setSupervising],
  );

  const clearRole = useCallback(() => {
    const key = roleKey(tag);
    if (key) localStorage.removeItem(key);
    setRoleState(null);
  }, [tag]);

  return (
    <RoleContext.Provider value={{ role, setRole, clearRole }}>{children}</RoleContext.Provider>
  );
};

export const useRole = (): UseRoleResult => useContext(RoleContext);

export default useRole;
